import API_CONFIG from '../config/api';

// Report exports need an OTP (see EXPORT OTP in backend/server.js). One
// verified code unlocks exports for a few minutes; the token lives in
// sessionStorage so a reload inside that window does not ask again, and it is
// tied to the signed-in admin so a different login on the same browser is
// prompted afresh. The owner account is exempt server-side, and that answer
// is cached the same way (as an empty token).
const STORAGE_KEY = 'adminExportToken';
const EXEMPT_TTL_MS = 8 * 60 * 60 * 1000;

let prompt = null;

const sessionUserId = () => {
    try {
        const session = JSON.parse(localStorage.getItem('adminSession') || 'null');
        return session?.userId || session?.id || null;
    } catch (e) {
        return null;
    }
};

export const exportTokenHeader = (token) => (token ? { 'x-export-token': token } : {});

export function readExportToken() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const saved = JSON.parse(raw);
        if (String(saved.userId) !== String(sessionUserId())) return null;
        if (Date.now() > Number(saved.expiresAt) - 5000) return null;
        return saved.exempt ? '' : (saved.token || null);
    } catch (e) {
        return null;
    }
}

export function saveExportToken({ token = '', expiresAt = 0, exempt = false }) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            userId: sessionUserId(),
            token,
            exempt,
            expiresAt: exempt ? Date.now() + EXEMPT_TTL_MS : expiresAt
        }));
    } catch (e) {
        /* a blocked sessionStorage only means one more prompt */
    }
}

export function clearExportToken() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
}

// The OTP modal (ExportOtpProvider in the dashboard layout) registers itself here.
export function registerExportOtpPrompt(fn) {
    prompt = fn;
    return () => { if (prompt === fn) prompt = null; };
}

export class ExportCancelled extends Error {
    constructor() {
        super('Export cancelled');
        this.cancelled = true;
    }
}

export const isExportCancelled = (error) => Boolean(error && error.cancelled);

// Resolves with the token to send (empty for the exempt owner) once the OTP
// has been passed; rejects with ExportCancelled if the admin closes the prompt.
export async function ensureExportToken(purpose) {
    const cached = readExportToken();
    if (cached !== null) return cached;
    if (!prompt) throw new Error('The export OTP prompt is not available on this page.');
    return prompt(purpose);
}

// Runs an export request with the token. If the server no longer accepts it
// (restart, expiry), asks for the OTP once more and retries.
export async function withExportOtp(purpose, run) {
    let token = await ensureExportToken(purpose);
    let res = await run(token);
    if (res && res.status === 403) {
        const body = await res.clone().json().catch(() => null);
        if (body?.code === 'EXPORT_OTP_REQUIRED') {
            clearExportToken();
            token = await ensureExportToken(purpose);
            res = await run(token);
        }
    }
    return res;
}

// Exports assembled in the browser (a PDF, a snapshot) record themselves
// here; the server still checks the token before writing the row.
export async function logBrowserExport(purpose, description, { targetType = null, targetId = null } = {}) {
    const res = await withExportOtp(purpose, (token) => fetch(`${API_CONFIG.BASE_URL}/api/admin/exports/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': sessionUserId(), ...exportTokenHeader(token) },
        body: JSON.stringify({ description, targetType, targetId })
    }));
    if (!res.ok) throw new Error('The export could not be verified');
    return res.json();
}
